import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes } from 'styled-components';

import bskyLogo from 'assets/windowsIcons/bluesky.svg';

const HANDLE = 'lottel.bsky.social';
const API = 'https://public.api.bsky.app/xrpc';
const PROFILE_URL = `https://bsky.app/profile/${HANDLE}`;
const PAGE_SIZE = 30;

const TABS = [
  {
    key: 'posts',
    label: 'Posts',
    filter: 'posts_and_author_threads',
    emptyMsg: 'No posts to show.',
  },
  {
    key: 'replies',
    label: 'Replies',
    filter: 'posts_with_replies',
    emptyMsg: 'No replies yet.',
  },
  {
    key: 'media',
    label: 'Media',
    filter: 'posts_with_media',
    emptyMsg: 'No media posts yet.',
  },
];

async function apiCall(endpoint, params, signal) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v == null) continue;
    if (Array.isArray(v)) v.forEach(x => qs.append(k, String(x)));
    else qs.append(k, String(v));
  }
  const res = await fetch(`${API}/${endpoint}?${qs}`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function fmtCount(n) {
  if (n == null) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const v = n / 1000;
    return `${v < 10 ? v.toFixed(1) : Math.round(v)}K`.replace('.0K', 'K');
  }
  return `${(n / 1_000_000).toFixed(1)}M`.replace('.0M', 'M');
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.round(mo / 12)}y`;
}

function fullTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function joinDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function openExternal(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function postWebUrl(post) {
  const rkey = post.uri.split('/').pop();
  return `https://bsky.app/profile/${post.author.handle}/post/${rkey}`;
}

function safeHost(url) {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch (e) {
    return '';
  }
}

function initial(name) {
  return (
    String(name || '?')
      .trim()
      .charAt(0) || '?'
  ).toUpperCase();
}

function shortenUrl(text, max = 40) {
  if (!text) return '';
  const clean = text.replace(/^https?:\/\//, '');
  return clean.length > max ? clean.slice(0, max) + 'â€¦' : clean;
}

function hideOnError(e) {
  e.currentTarget.style.visibility = 'hidden';
}

function segmentByFacets(text, facets) {
  if (!text) return [];
  if (!facets || !facets.length) return [{ type: 'text', text }];
  const bytes = encoder.encode(text);
  const sorted = facets
    .filter(f => f && f.index && f.features && f.features.length)
    .slice()
    .sort((a, b) => a.index.byteStart - b.index.byteStart);
  const out = [];
  let cursor = 0;
  for (const f of sorted) {
    const { byteStart, byteEnd } = f.index;
    if (byteStart < cursor || byteEnd > bytes.length || byteEnd <= byteStart)
      continue;
    if (byteStart > cursor) {
      out.push({
        type: 'text',
        text: decoder.decode(bytes.slice(cursor, byteStart)),
      });
    }
    const chunk = decoder.decode(bytes.slice(byteStart, byteEnd));
    const feat = f.features[0];
    switch (feat.$type) {
      case 'app.bsky.richtext.facet#link':
        out.push({ type: 'link', text: chunk, uri: feat.uri });
        break;
      case 'app.bsky.richtext.facet#mention':
        out.push({ type: 'mention', text: chunk, did: feat.did });
        break;
      case 'app.bsky.richtext.facet#tag':
        out.push({ type: 'tag', text: chunk, tag: feat.tag });
        break;
      default:
        out.push({ type: 'text', text: chunk });
    }
    cursor = byteEnd;
  }
  if (cursor < bytes.length) {
    out.push({ type: 'text', text: decoder.decode(bytes.slice(cursor)) });
  }
  return out;
}

function RichText({ text, facets }) {
  const parts = useMemo(() => segmentByFacets(text, facets), [text, facets]);
  return parts.map((p, i) => {
    if (p.type === 'link') {
      return (
        <FacetLink
          key={i}
          type="button"
          onClick={e => {
            e.stopPropagation();
            openExternal(p.uri);
          }}
          title={p.uri}
        >
          {shortenUrl(p.text)}
        </FacetLink>
      );
    }
    if (p.type === 'mention') {
      const h = p.text.replace(/^@/, '');
      return (
        <FacetLink
          key={i}
          type="button"
          onClick={e => {
            e.stopPropagation();
            openExternal(`https://bsky.app/profile/${h}`);
          }}
        >
          {p.text}
        </FacetLink>
      );
    }
    if (p.type === 'tag') {
      return (
        <FacetLink
          key={i}
          type="button"
          onClick={e => {
            e.stopPropagation();
            openExternal(
              `https://bsky.app/hashtag/${encodeURIComponent(p.tag)}`,
            );
          }}
        >
          {p.text}
        </FacetLink>
      );
    }
    return <React.Fragment key={i}>{p.text}</React.Fragment>;
  });
}

const AUTOLINK_RE = /(https?:\/\/[^\s<>()]+)|(#[\p{L}\p{N}_-]+)|(@[a-z0-9][a-z0-9.-]*\.[a-z]{2,})/giu;

function AutoLinkText({ text }) {
  if (!text) return null;
  const nodes = [];
  let last = 0;
  let i = 0;
  const re = new RegExp(AUTOLINK_RE.source, AUTOLINK_RE.flags);
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(
        <React.Fragment key={`t${i++}`}>
          {text.slice(last, m.index)}
        </React.Fragment>,
      );
    }
    let match = m[0];
    let tail = '';
    if (match.startsWith('http')) {
      const trailingMatch = match.match(/[.,;!?)\]]+$/);
      if (trailingMatch) {
        tail = trailingMatch[0];
        match = match.slice(0, -tail.length);
      }
      nodes.push(
        <FacetLink
          key={`u${i++}`}
          type="button"
          onClick={e => {
            e.stopPropagation();
            openExternal(match);
          }}
          title={match}
        >
          {shortenUrl(match)}
        </FacetLink>,
      );
    } else if (match.startsWith('#')) {
      const tag = match.slice(1);
      nodes.push(
        <FacetLink
          key={`h${i++}`}
          type="button"
          onClick={e => {
            e.stopPropagation();
            openExternal(`https://bsky.app/hashtag/${encodeURIComponent(tag)}`);
          }}
        >
          {match}
        </FacetLink>,
      );
    } else if (match.startsWith('@')) {
      const h = match.slice(1);
      nodes.push(
        <FacetLink
          key={`m${i++}`}
          type="button"
          onClick={e => {
            e.stopPropagation();
            openExternal(`https://bsky.app/profile/${h}`);
          }}
        >
          {match}
        </FacetLink>,
      );
    }
    if (tail) {
      nodes.push(<React.Fragment key={`tl${i++}`}>{tail}</React.Fragment>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    nodes.push(
      <React.Fragment key={`end${i++}`}>{text.slice(last)}</React.Fragment>,
    );
  }
  return nodes;
}

const iconAttrs = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

function IconReply(props) {
  return (
    <svg width={16} height={16} {...iconAttrs} {...props}>
      <path d="M20 17.5a3 3 0 0 1-3 3H8.5L4 24V6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5z" />
    </svg>
  );
}
function IconRepost(props) {
  return (
    <svg width={16} height={16} {...iconAttrs} {...props}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}
function IconHeart(props) {
  return (
    <svg width={16} height={16} {...iconAttrs} {...props}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function IconRefresh(props) {
  return (
    <svg width={16} height={16} {...iconAttrs} {...props}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}
function IconPin(props) {
  return (
    <svg width={12} height={12} {...iconAttrs} {...props}>
      <path d="M17 3l4 4-6 2-4 4-1 5-3-3-6 6 6-6-3-3 5-1 4-4 2-6z" />
    </svg>
  );
}
function IconPlay(props) {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      {...props}
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
function IconThread(props) {
  return (
    <svg width={12} height={12} {...iconAttrs} {...props}>
      <path d="M9 17l-6-6 6-6" />
      <path d="M21 17V9a4 4 0 0 0-4-4H3" />
    </svg>
  );
}
function IconQuote(props) {
  return (
    <svg width={16} height={16} {...iconAttrs} {...props}>
      <path d="M3 21c3 0 7-1 7-8V5H4v8h4M14 21c3 0 7-1 7-8V5h-6v8h4" />
    </svg>
  );
}

function LoadingSkeleton() {
  return (
    <>
      <SkelBanner />
      <ProfileHead>
        <AvatarRow>
          <SkelAvatar />
          <SkelPill />
        </AvatarRow>
        <SkelLine w="55%" h={20} />
        <SkelLine w="35%" h={14} mt={6} />
        <SkelStatsRow>
          <SkelLine w="72px" h={14} />
          <SkelLine w="72px" h={14} />
          <SkelLine w="60px" h={14} />
        </SkelStatsRow>
        <SkelLine w="90%" h={14} mt={16} />
        <SkelLine w="70%" h={14} mt={6} />
      </ProfileHead>
      <SkelTabsRow>
        <SkelLine w="30%" h={14} />
        <SkelLine w="30%" h={14} />
        <SkelLine w="30%" h={14} />
      </SkelTabsRow>
      {[0, 1, 2].map(i => (
        <SkelPost key={i}>
          <SkelPostHead>
            <SkelPostAvatar />
            <div style={{ flex: 1 }}>
              <SkelLine w="40%" h={12} />
              <SkelLine w="55%" h={11} mt={4} />
            </div>
          </SkelPostHead>
          <SkelLine w="95%" h={12} mt={8} />
          <SkelLine w="80%" h={12} mt={5} />
          <SkelLine w="60%" h={12} mt={5} />
        </SkelPost>
      ))}
    </>
  );
}

function Bluesky() {
  const [profile, setProfile] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [feed, setFeed] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const abortRef = useRef(null);

  const load = useCallback(tab => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);
    setFeed([]);
    setCursor(null);
    setPinned(null);

    const filter = TABS.find(t => t.key === tab).filter;

    Promise.all([
      apiCall('app.bsky.actor.getProfile', { actor: HANDLE }, ctrl.signal),
      apiCall(
        'app.bsky.feed.getAuthorFeed',
        { actor: HANDLE, limit: PAGE_SIZE, filter },
        ctrl.signal,
      ),
    ])
      .then(async ([p, f]) => {
        if (ctrl.signal.aborted) return;
        setProfile(p);
        setFeed(f.feed || []);
        setCursor(f.cursor || null);

        if (tab === 'posts' && p.pinnedPost && p.pinnedPost.uri) {
          try {
            const pins = await apiCall(
              'app.bsky.feed.getPosts',
              { uris: p.pinnedPost.uri },
              ctrl.signal,
            );
            if (!ctrl.signal.aborted && pins.posts && pins.posts[0]) {
              setPinned(pins.posts[0]);
            }
          } catch (e) {}
        }
        setLoading(false);
      })
      .catch(e => {
        if (e.name === 'AbortError') return;
        setError(e.message || 'Failed to load Bluesky feed');
        setLoading(false);
      });
  }, []);

  const loadMore = useCallback(() => {
    if (loadingMore || !cursor) return;
    setLoadingMore(true);
    const ctrl = new AbortController();
    const filter = TABS.find(t => t.key === activeTab).filter;
    apiCall(
      'app.bsky.feed.getAuthorFeed',
      { actor: HANDLE, limit: PAGE_SIZE, filter, cursor },
      ctrl.signal,
    )
      .then(f => {
        setFeed(prev => [...prev, ...(f.feed || [])]);
        setCursor(f.cursor || null);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  }, [cursor, activeTab, loadingMore]);

  useEffect(() => {
    load(activeTab);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load, activeTab]);

  const displayFeed = useMemo(() => {
    if (!pinned) return feed;
    const pinnedUri = pinned.uri;
    return [
      { post: pinned, __pinned: true },
      ...feed.filter(item => item.post.uri !== pinnedUri),
    ];
  }, [pinned, feed]);

  return (
    <Div>
      <Toolbar>
        <ToolbarLeft
          type="button"
          onClick={() => openExternal(PROFILE_URL)}
          title={`Open @${HANDLE} on bsky.app`}
        >
          <ToolbarLogo src={bskyLogo} alt="Bluesky" />
          <HandleLabel>@{HANDLE}</HandleLabel>
        </ToolbarLeft>
        <IconBtn
          type="button"
          onClick={() => load(activeTab)}
          disabled={loading}
          aria-label="Refresh"
          title="Refresh"
        >
          {loading ? (
            <Spinner>
              <IconRefresh />
            </Spinner>
          ) : (
            <IconRefresh />
          )}
        </IconBtn>
      </Toolbar>

      <Scroll>
        {error && !profile && (
          <ErrorPane>
            <div>Couldn't load @{HANDLE}</div>
            <small>{error}</small>
            <button type="button" onClick={() => load(activeTab)}>
              Retry
            </button>
          </ErrorPane>
        )}

        {loading && !profile && !error && <LoadingSkeleton />}

        {profile && (
          <>
            <Banner
              style={
                profile.banner
                  ? { backgroundImage: `url(${profile.banner})` }
                  : undefined
              }
            />
            <ProfileHead>
              <AvatarRow>
                <Avatar profile={profile} />
                <FollowBtn
                  type="button"
                  onClick={() => openExternal(PROFILE_URL)}
                >
                  Follow
                </FollowBtn>
              </AvatarRow>
              <Name>{profile.displayName || profile.handle}</Name>
              <Handle>@{profile.handle}</Handle>
              <Stats>
                <StatBtn
                  type="button"
                  onClick={() => openExternal(`${PROFILE_URL}/followers`)}
                >
                  <b>{fmtCount(profile.followersCount)}</b> followers
                </StatBtn>
                <StatBtn
                  type="button"
                  onClick={() => openExternal(`${PROFILE_URL}/follows`)}
                >
                  <b>{fmtCount(profile.followsCount)}</b> following
                </StatBtn>
                <StatText>
                  <b>{fmtCount(profile.postsCount)}</b> posts
                </StatText>
              </Stats>
              {profile.description && (
                <Bio>
                  <AutoLinkText text={profile.description} />
                </Bio>
              )}
              {profile.createdAt && (
                <Joined>Joined {joinDate(profile.createdAt)}</Joined>
              )}
            </ProfileHead>

            <Tabs>
              {TABS.map(t => (
                <Tab
                  key={t.key}
                  type="button"
                  active={activeTab === t.key}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </Tab>
              ))}
            </Tabs>

            {loading && !error && <FeedLoading>Loadingâ€¦</FeedLoading>}

            {error && profile && (
              <FeedError>
                Couldn't load feed.{' '}
                <button type="button" onClick={() => load(activeTab)}>
                  Retry
                </button>
              </FeedError>
            )}

            <FeedList>
              {!loading && displayFeed.length === 0 && !error && (
                <EmptyState>
                  {TABS.find(t => t.key === activeTab).emptyMsg}
                </EmptyState>
              )}
              {displayFeed.map((item, i) => (
                <PostRow key={`${item.post.uri}_${i}`} item={item} />
              ))}
            </FeedList>

            {cursor && !error && !loading && (
              <LoadMoreBtn
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loadingâ€¦' : 'Load more posts'}
              </LoadMoreBtn>
            )}

            <FooterNote>
              via AT Protocol Â·{' '}
              <FooterLink
                type="button"
                onClick={() => openExternal(PROFILE_URL)}
              >
                Open on bsky.app â†—
              </FooterLink>
            </FooterNote>
          </>
        )}
      </Scroll>
    </Div>
  );
}

function Avatar({ profile }) {
  const [failed, setFailed] = useState(false);
  if (!profile.avatar || failed) {
    return (
      <AvatarFallback>
        {initial(profile.displayName || profile.handle)}
      </AvatarFallback>
    );
  }
  return (
    <AvatarImg src={profile.avatar} alt="" onError={() => setFailed(true)} />
  );
}

function SmallAvatar({ author }) {
  const [failed, setFailed] = useState(false);
  if (!author.avatar || failed) {
    return (
      <PostAvatarFallback>
        {initial(author.displayName || author.handle)}
      </PostAvatarFallback>
    );
  }
  return (
    <PostAvatarImg src={author.avatar} alt="" onError={() => setFailed(true)} />
  );
}

function PostRow({ item }) {
  const { post, reason, reply, __pinned } = item;
  const author = post.author;
  const record = post.record || {};
  const embed = post.embed;
  const url = postWebUrl(post);
  const createdAt = record.createdAt || post.indexedAt;
  const isThreadContinuation =
    reply &&
    reply.parent &&
    reply.parent.author &&
    reply.parent.author.did === author.did;

  function onCardClick(e) {
    if (e.target.closest('button, a')) return;
    openExternal(url);
  }

  return (
    <PostCard onClick={onCardClick}>
      {__pinned && (
        <ContextBadge>
          <IconPin /> Pinned
        </ContextBadge>
      )}
      {reason && reason.$type === 'app.bsky.feed.defs#reasonRepost' && (
        <ContextBadge>
          <IconRepost width={12} height={12} />
          Reposted by {reason.by.displayName || `@${reason.by.handle}`}
        </ContextBadge>
      )}
      {isThreadContinuation && (
        <ContextBadge>
          <IconThread />
          Thread reply
        </ContextBadge>
      )}
      <PostHead>
        <SmallAvatar author={author} />
        <PostAuthorNames>
          <PostAuthorName>{author.displayName || author.handle}</PostAuthorName>
          <PostAuthorMeta>
            @{author.handle}
            {' Â· '}
            <PostTime title={fullTime(createdAt)}>
              {relTime(createdAt)}
            </PostTime>
          </PostAuthorMeta>
        </PostAuthorNames>
      </PostHead>
      {reply && reply.parent && reply.parent.author && !isThreadContinuation && (
        <ReplyContext>
          Replying to{' '}
          <FacetLink
            type="button"
            onClick={e => {
              e.stopPropagation();
              openExternal(
                `https://bsky.app/profile/${reply.parent.author.handle}`,
              );
            }}
          >
            @{reply.parent.author.handle}
          </FacetLink>
        </ReplyContext>
      )}
      {record.text && (
        <PostText>
          <RichText text={record.text} facets={record.facets} />
        </PostText>
      )}
      {embed && <PostEmbed embed={embed} />}
      <PostStats>
        <PostStat>
          <IconReply /> {fmtCount(post.replyCount || 0)}
        </PostStat>
        <PostStat>
          <IconRepost /> {fmtCount(post.repostCount || 0)}
        </PostStat>
        {post.quoteCount > 0 && (
          <PostStat>
            <IconQuote /> {fmtCount(post.quoteCount)}
          </PostStat>
        )}
        <PostStat>
          <IconHeart /> {fmtCount(post.likeCount || 0)}
        </PostStat>
      </PostStats>
    </PostCard>
  );
}

function PostEmbed({ embed }) {
  if (!embed) return null;
  const t = embed.$type;

  if (
    t === 'app.bsky.embed.images#view' ||
    t === 'app.bsky.embed.gallery#view'
  ) {
    const imgs =
      (t === 'app.bsky.embed.images#view' ? embed.images : embed.items) || [];
    const single = imgs.length === 1;
    return (
      <EmbedImages count={imgs.length}>
        {imgs.map((img, i) => (
          <ImageEmbed key={i} img={img} single={single} />
        ))}
      </EmbedImages>
    );
  }

  if (t === 'app.bsky.embed.video#view') {
    return (
      <VideoEmbed>
        {embed.thumbnail && (
          <img src={embed.thumbnail} alt="" onError={hideOnError} />
        )}
        <PlayGlyph>
          <IconPlay />
        </PlayGlyph>
      </VideoEmbed>
    );
  }

  if (t === 'app.bsky.embed.external#view') {
    const ext = embed.external || {};
    return (
      <ExternalCard
        type="button"
        onClick={e => {
          e.stopPropagation();
          if (ext.uri) openExternal(ext.uri);
        }}
      >
        {ext.thumb && <ExtThumb src={ext.thumb} alt="" onError={hideOnError} />}
        <ExtBody>
          <ExtHost>{safeHost(ext.uri)}</ExtHost>
          <ExtTitle>{ext.title || ext.uri}</ExtTitle>
          {ext.description && <ExtDesc>{ext.description}</ExtDesc>}
        </ExtBody>
      </ExternalCard>
    );
  }

  if (t === 'app.bsky.embed.record#view') {
    return <QuoteEmbed record={embed.record} />;
  }

  if (t === 'app.bsky.embed.recordWithMedia#view') {
    return (
      <>
        {embed.media && <PostEmbed embed={embed.media} />}
        {embed.record && <QuoteEmbed record={embed.record.record} />}
      </>
    );
  }

  return null;
}

function ImageEmbed({ img, single }) {
  const [showAlt, setShowAlt] = useState(false);
  const src = img.thumb || img.thumbnail;
  const alt = img.alt || '';
  const ar = img.aspectRatio;
  const wrapStyle =
    single && ar && ar.width && ar.height
      ? { aspectRatio: `${ar.width} / ${ar.height}` }
      : undefined;
  return (
    <EmbedImageWrap style={wrapStyle} single={single}>
      <EmbedImage src={src} alt={alt} onError={hideOnError} single={single} />
      {alt && (
        <>
          <AltBadge
            type="button"
            onClick={e => {
              e.stopPropagation();
              setShowAlt(v => !v);
            }}
            aria-pressed={showAlt}
            title={showAlt ? 'Hide alt text' : 'Show alt text'}
          >
            ALT
          </AltBadge>
          {showAlt && (
            <AltOverlay onClick={e => e.stopPropagation()}>{alt}</AltOverlay>
          )}
        </>
      )}
    </EmbedImageWrap>
  );
}

function QuoteEmbed({ record }) {
  if (!record) return null;
  switch (record.$type) {
    case 'app.bsky.embed.record#viewNotFound':
      return <QuoteBox muted>Deleted post</QuoteBox>;
    case 'app.bsky.embed.record#viewBlocked':
      return <QuoteBox muted>Blocked post</QuoteBox>;
    case 'app.bsky.embed.record#viewDetached':
      return <QuoteBox muted>Detached quote</QuoteBox>;
    case 'app.bsky.embed.record#viewRecord':
      break;
    default:
      return <QuoteBox muted>Quoted post unavailable</QuoteBox>;
  }
  const author = record.author || {};
  const value = record.value || {};
  return (
    <QuoteBox
      as="button"
      type="button"
      onClick={e => {
        e.stopPropagation();
        const rkey = record.uri && record.uri.split('/').pop();
        if (rkey && author.handle) {
          openExternal(
            `https://bsky.app/profile/${author.handle}/post/${rkey}`,
          );
        }
      }}
    >
      <QuoteHead>
        {author.avatar ? (
          <QuoteAvatar src={author.avatar} alt="" onError={hideOnError} />
        ) : (
          <QuoteAvatarFallback>
            {initial(author.displayName || author.handle)}
          </QuoteAvatarFallback>
        )}
        <QuoteAuthor>
          <b>{author.displayName || author.handle}</b>
          <span> @{author.handle}</span>
        </QuoteAuthor>
      </QuoteHead>
      {value.text && (
        <QuoteText>
          <RichText text={value.text} facets={value.facets} />
        </QuoteText>
      )}
      {record.embeds && record.embeds[0] && (
        <PostEmbed embed={record.embeds[0]} />
      )}
    </QuoteBox>
  );
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Div = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #0d1418;
  color: #e6e9ea;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  font-size: 14px;
`;

const Toolbar = styled.div`
  height: 40px;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  padding: 0 8px 0 12px;
  gap: 8px;
  background: #161e27;
  border-bottom: 1px solid #212a31;
`;

const ToolbarLeft = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 4px 6px;
  margin-left: -6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-align: left;
  &:hover {
    background: #1c242c;
  }
`;

const ToolbarLogo = styled.img`
  width: 20px;
  height: 18px;
`;

const HandleLabel = styled.span`
  flex: 1;
  min-width: 0;
  color: #e6e9ea;
  font-weight: 700;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #e6e9ea;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
  &:not(:disabled):hover {
    background: #1c242c;
  }
`;

const Spinner = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`;

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: #37414a #0d1418;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #0d1418;
  }
  &::-webkit-scrollbar-thumb {
    background: #2b333a;
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #37414a;
  }
`;

const FeedLoading = styled.div`
  padding: 24px;
  text-align: center;
  color: #8a99a7;
  font-size: 13px;
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const SkelBase = styled.div`
  background: linear-gradient(90deg, #161e27 0%, #212a31 50%, #161e27 100%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s linear infinite;
  border-radius: 4px;
`;

const SkelBanner = styled(SkelBase)`
  height: 120px;
  border-radius: 0;
`;

const SkelAvatar = styled(SkelBase)`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 3px solid #0d1418;
`;

const SkelPill = styled(SkelBase)`
  width: 80px;
  height: 32px;
  border-radius: 16px;
`;

const SkelLine = styled(SkelBase)`
  height: ${p => p.h || 12}px;
  width: ${p => p.w || '100%'};
  margin-top: ${p => p.mt || 0}px;
`;

const SkelPost = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #1c242c;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SkelPostHead = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SkelPostAvatar = styled(SkelBase)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const SkelStatsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 10px;
`;

const SkelTabsRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #212a31;
`;

const FeedError = styled.div`
  padding: 20px;
  text-align: center;
  color: #ff9a9a;
  font-size: 13px;
  button {
    margin-left: 8px;
    background: none;
    border: 1px solid #5a2a2a;
    color: #ff9a9a;
    border-radius: 12px;
    padding: 4px 10px;
    font: inherit;
    cursor: pointer;
    &:hover {
      background: #2a1414;
    }
  }
`;

const ErrorPane = styled.div`
  margin: 20px;
  padding: 16px;
  border: 1px solid #5a2a2a;
  background: #2a1414;
  border-radius: 8px;
  color: #ff9a9a;
  display: flex;
  flex-direction: column;
  gap: 8px;
  small {
    color: #c07070;
  }
  button {
    align-self: flex-start;
    padding: 6px 14px;
    border: 1px solid #2b333a;
    background: #0d1418;
    color: #e6e9ea;
    border-radius: 12px;
    cursor: pointer;
    font: inherit;
    &:hover {
      background: #1c242c;
    }
  }
`;

const Banner = styled.div`
  height: 120px;
  background: linear-gradient(135deg, #0d3a63, #1083fe);
  background-size: cover;
  background-position: center;
`;

const ProfileHead = styled.div`
  padding: 0 14px 14px;
  position: relative;
`;

const AvatarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: -38px;
  margin-bottom: 8px;
`;

const AvatarImg = styled.img`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 3px solid #0d1418;
  background: #212a31;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 3px solid #0d1418;
  background: #212a31;
  color: #e6e9ea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
`;

const FollowBtn = styled.button`
  padding: 7px 20px;
  background: #1083fe;
  color: #fff;
  border: none;
  border-radius: 20px;
  font: 700 13px 'Segoe UI', Tahoma, sans-serif;
  cursor: pointer;
  &:hover {
    background: #0d6fdc;
  }
`;

const Name = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #f1f3f5;
`;

const Handle = styled.div`
  color: #8a99a7;
  font-size: 14px;
  margin-top: 2px;
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
  font-size: 13px;
  color: #8a99a7;
  b {
    color: #f1f3f5;
    margin-right: 3px;
  }
`;

const StatBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const StatText = styled.span``;

const Bio = styled.div`
  margin-top: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
  font-size: 14px;
  color: #d1d6da;
`;

const Joined = styled.div`
  margin-top: 8px;
  color: #8a99a7;
  font-size: 12px;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #212a31;
  background: #0d1418;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 4px;
  border: none;
  background: none;
  font: 700 13px 'Segoe UI', Tahoma, sans-serif;
  color: ${p => (p.active ? '#f1f3f5' : '#8a99a7')};
  cursor: pointer;
  border-bottom: 3px solid ${p => (p.active ? '#1083fe' : 'transparent')};
  &:hover {
    background: #141c25;
    color: #e6e9ea;
  }
`;

const FeedList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #8a99a7;
`;

const PostCard = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #1c242c;
  cursor: pointer;
  &:hover {
    background: #141c25;
  }
`;

const ContextBadge = styled.div`
  font-size: 12px;
  color: #8a99a7;
  margin-bottom: 6px;
  padding-left: 28px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PostHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const PostAvatarImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #212a31;
  object-fit: cover;
  flex-shrink: 0;
`;

const PostAvatarFallback = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #212a31;
  color: #e6e9ea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
`;

const PostAuthorNames = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
`;

const PostAuthorName = styled.div`
  font-weight: 700;
  color: #f1f3f5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PostAuthorMeta = styled.div`
  color: #8a99a7;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PostTime = styled.span`
  cursor: help;
`;

const ReplyContext = styled.div`
  color: #8a99a7;
  font-size: 12px;
  margin-bottom: 4px;
`;

const PostText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
  margin: 4px 0;
  color: #e6e9ea;
`;

const FacetLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: #1083fe;
  cursor: pointer;
  vertical-align: baseline;
  &:hover {
    text-decoration: underline;
  }
`;

const EmbedImages = styled.div`
  display: grid;
  gap: 4px;
  margin-top: 8px;
  border-radius: 10px;
  overflow: hidden;
  grid-template-columns: ${p => (p.count === 1 ? '1fr' : 'repeat(2, 1fr)')};
`;

const EmbedImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  background: #212a31;
  ${p =>
    !p.single &&
    `
    height: 170px;
  `}
`;

const EmbedImage = styled.img`
  width: 100%;
  display: block;
  ${p =>
    p.single
      ? `
    height: auto;
    max-height: 480px;
    object-fit: contain;
    background: #0d1418;
  `
      : `
    height: 100%;
    object-fit: cover;
  `}
`;

const AltBadge = styled.button`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const AltOverlay = styled.div`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 32px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: 12px;
  border-radius: 8px;
  line-height: 1.4;
  max-height: 60%;
  overflow-y: auto;
`;

const VideoEmbed = styled.div`
  position: relative;
  margin-top: 8px;
  border-radius: 10px;
  overflow: hidden;
  background: #000;
  img {
    width: 100%;
    max-height: 340px;
    object-fit: cover;
    display: block;
  }
`;

const PlayGlyph = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 3px;
`;

const ExternalCard = styled.button`
  display: flex;
  margin-top: 8px;
  border: 1px solid #2b333a;
  border-radius: 10px;
  overflow: hidden;
  background: #161e27;
  text-align: left;
  cursor: pointer;
  padding: 0;
  font: inherit;
  color: #e6e9ea;
  width: 100%;
  &:hover {
    border-color: #1083fe;
    background: #1c242c;
  }
`;

const ExtThumb = styled.img`
  width: 88px;
  height: 88px;
  object-fit: cover;
  flex-shrink: 0;
  background: #212a31;
`;

const ExtBody = styled.div`
  padding: 8px 10px;
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ExtHost = styled.div`
  color: #8a99a7;
  font-size: 12px;
`;

const ExtTitle = styled.div`
  font-weight: 600;
  color: #f1f3f5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ExtDesc = styled.div`
  color: #8a99a7;
  font-size: 13px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const QuoteBox = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #2b333a;
  border-radius: 10px;
  background: #161e27;
  ${p => p.muted && 'color: #8a99a7; font-style: italic;'}
`;

const QuoteHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  margin-bottom: 4px;
`;

const QuoteAvatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #212a31;
`;

const QuoteAvatarFallback = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #212a31;
  color: #e6e9ea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
`;

const QuoteAuthor = styled.span`
  color: #8a99a7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  b {
    color: #f1f3f5;
    margin-right: 4px;
  }
`;

const QuoteText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  font-size: 13px;
  color: #d1d6da;
`;

const PostStats = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 10px;
  color: #8a99a7;
  font-size: 13px;
`;

const PostStat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LoadMoreBtn = styled.button`
  display: block;
  margin: 12px auto 8px;
  padding: 8px 20px;
  border: 1px solid #2b333a;
  background: transparent;
  color: #1083fe;
  border-radius: 20px;
  font: 600 13px 'Segoe UI', Tahoma, sans-serif;
  cursor: pointer;
  &:disabled {
    color: #5b6b78;
    cursor: default;
  }
  &:not(:disabled):hover {
    background: #141c25;
    border-color: #37414a;
  }
`;

const FooterNote = styled.div`
  padding: 12px 16px 20px;
  color: #8a99a7;
  font-size: 12px;
  text-align: center;
  border-top: 1px solid #1c242c;
`;

const FooterLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: #1083fe;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export default Bluesky;
